/**
 * Prevent subnavidation hiding when scrolling past footer
 *
 */
function PreventSubnavigationHiding() {
  /**
   * @param {HTMLElement[]} entries DOM nodes
   */
  const handleIntersection = (entries) => {
    entries.forEach((entry) => {
      const section = document.querySelector('.at-scroll-to-section');
      if (entry.isIntersecting) {
        section.style.position = 'fixed';
        section.style.left = 0;
        section.style.right = 0;
        // Create placeholder
        const newDiv = document.createElement('div');
        newDiv.textContent = '&nbsp;';
        const sectionHeight = section.offsetHeight;
        newDiv.style.height = `${sectionHeight}px`;
        newDiv.classList.add('at-placeholder');
        section.parentNode.insertBefore(newDiv, section);
      } else {
        const placeholder = document.querySelector('.at-placeholder');
        if (!placeholder) return;
        section.style.position = 'sticky';
        placeholder.remove();
      }
    });
  };
  const observer = new IntersectionObserver((entries) => handleIntersection(entries), {
    root: null,
    threshold: 0.1,
  });
  [document.querySelector('.at-footer-wrapper')].forEach((section) => observer.observe(section));
}

/**
 *  When all links are visible(containers are equal) then disable slider and stretch links(fill empty space)
 *
 */
function addResizeObserver() {
  const slider = document.querySelector('.at-scroll-to-section__inner at-scroll-slider');
  if (!slider) return;

  let isChecking = false;

  const resizeObserver = new ResizeObserver((entries) => {
    if (isChecking) return;
    isChecking = true;

    // eslint-disable-next-line
    for (let entry of entries) {
      const track = entry.target.querySelector('.at-scroll-slider__track');
      if (!track) continue;

      // Remove stretch-slides to measure natural content width
      // (with stretch-slides active, width:100% makes scrollWidth === clientWidth always)
      entry.target.classList.remove('stretch-slides');

      // Reading scrollWidth forces synchronous layout recalculation
      const needsScroll = track.scrollWidth > track.clientWidth;

      if (!needsScroll) {
        entry.target.classList.add('stretch-slides');
      }
    }

    requestAnimationFrame(() => { isChecking = false; });
  });
  resizeObserver.observe(slider);
}

/**
 *  Intersection observer
 *
 * @param {HTMLElement[]} sections DOM nodes
 * @param {HTMLElement[]} links navigation links
 */
function addIntersectionObserver(sections, links) {
  /**
   * @param {string} sectionId ID of the section to highlight
   */
  const highlightLink = (sectionId) => {
    const link = document.querySelector(`.at-scroll-to-section__inner a[href='#${sectionId}']`);
    if (!link) return;
    links.forEach((linkItem) => linkItem.classList.remove('at-highlight'));
    link.classList.add('at-highlight');
    const slider = document.querySelector('.at-scroll-to-section__inner at-scroll-slider');
    const track = slider?.querySelector('.at-scroll-slider__track');
    if (slider && track && track.scrollWidth > track.clientWidth) {
      slider.scrollTo(parseInt(link.dataset.index, 10));
    }
  };

  /** @type {Set<string>} Tracks sections that have been intersecting at least once */
  const seenSections = new Set();

  /**
   * @param {HTMLElement[]} entries DOM nodes
   */
  const handleIntersection = (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        seenSections.add(entry.target.id);
        highlightLink(entry.target.id);
      } else if (seenSections.has(entry.target.id)) {
        seenSections.delete(entry.target.id);

        if (entry.boundingClientRect.top > (entry.rootBounds?.bottom ?? 0)) {
          // Section exited below the observation line (user scrolled up)
          // Highlight the previous section that has a navigation link
          const sectionIndex = sections.indexOf(entry.target);
          for (let i = sectionIndex - 1; i >= 0; i--) {
            if (document.querySelector(`.at-scroll-to-section__inner a[href='#${sections[i].id}']`)) {
              highlightLink(sections[i].id);
              break;
            }
          }
        }


      }
    });
  };

  const observer = new IntersectionObserver((entries) => handleIntersection(entries), {
    root: null,
    rootMargin: `-250px 0px ${-1 * window.innerHeight + 250}px 0px`,
    threshold: 0,
  });
  sections.forEach((section) => observer.observe(section));
}

/**
 * Create DOM links
 *
 * @param {HTMLElement[]} sections DOM nodes
 * @returns {HTMLElement[]} array of links
 */
function createNavigationLinks(sections) {
  const repeatedSectionTitles = [];
  let index = 0;

  return sections
    .filter((section) => {
      if (!section.querySelector('[data-section-title]')?.dataset.sectionTitle) return false;
      return true;
    })
    .reduce((acc, section) => {
      const a = document.createElement('a');
      a.innerHTML = section.querySelector('[data-section-title]')?.dataset.sectionTitle;
      a.href = `#${section.id}`;
      a.dataset.index = index;
      a.classList.add('at-scroll-slider__slide');
      if (index === 0) a.classList.add('at-highlight');
      if (!repeatedSectionTitles.includes(section.querySelector('[data-section-title]')?.dataset.sectionTitle)) {
        acc.push(a);
        index += 1;
      }
      repeatedSectionTitles.push(section.querySelector('[data-section-title]')?.dataset.sectionTitle);
      return acc;
    }, []);
}

/**
 * Return all sibling sections (before and after element)
 *
 * @param {HTMLElement} element DOM node
 * @returns {HTMLElement[]} siblings to element in DOM order
 */
function getAllSiblingSections(element) {
  const siblings = [];
  const parent = element.parentElement;
  if (!parent) return siblings;
  for (const child of parent.children) {
    if (child !== element) {
      siblings.push(child);
    }
  }
  return siblings;
}

/**
 * Updates UI with new navigation links
 *
 * @param {HTMLElement[]} navigationLinks navigation links
 */
function updateNavigationUI(navigationLinks) {
  const container = document.querySelector('.at-scroll-to-section__inner');
  const template = document.getElementById('code-template').content.cloneNode(true);
  navigationLinks.forEach((link) => template.querySelector('.at-scroll-slider__slides').appendChild(link));
  container.appendChild(template);
}

/**
 * Scroll to section after clicking adequate link
 *
 * @param {HTMLElement[]} navigationLinks navigation links
 */
function addScrollBehavior(navigationLinks) {
  navigationLinks.forEach((link) => {
    let startX = 0;
    let startY = 0;

    link.addEventListener('pointerdown', (e) => {
      startX = e.clientX;
      startY = e.clientY;
    });

    // Prevent default anchor hash navigation
    link.addEventListener('click', (e) => e.preventDefault());

    // Use pointerup instead of click to bypass at-scroll-slider drag capture
    link.addEventListener('pointerup', (e) => {
      const dx = Math.abs(e.clientX - startX);
      const dy = Math.abs(e.clientY - startY);
      if (dx > 10 || dy > 10) return;

      const targetSection = document.querySelector(link.getAttribute('href'));
      if (!targetSection) return;
      const sectionTop = targetSection.getBoundingClientRect().top;
      const navHeight = document.querySelector('.at-scroll-to-section__inner')?.offsetHeight || 0;
      const header = document.querySelector('.shopify-section-header-sticky');
      const headerHeight = header ? header.offsetHeight : 0;
      window.scrollTo({
        top: window.scrollY + sectionTop - navHeight - (headerHeight / 2),
        behavior: 'smooth',
      });
    });
  });
}

/**
 * Hides .header-bottom when sticky nav is active by observing a sentinel element
 *
 * @param {HTMLElement} parentSection The .at-scroll-to-section section element
 * @param {string|null} sentinelClass CSS class of the sentinel divider placed before this section
 */
function setupHeaderBottomHiding(parentSection, sentinelClass) {
  const headerBottom = document.querySelector('.header-bottom');
  if (!headerBottom) return;

  const headerBottomNaturalHeight = headerBottom.offsetHeight;
  const stickyTop = headerBottom.getBoundingClientRect().top;

  // Captured once when section is in natural (non-sticky) flow position
  let sectionNaturalDocY = null;

  const update = () => {
    if (window.innerWidth < 750) {
      headerBottom.style.height = '';
      headerBottom.style.overflow = '';
      document.body.classList.remove('at-sticky-nav-active');
      return;
    }

    const sectionViewportTop = parentSection.getBoundingClientRect().top;

    // Update natural position only while section hasn't started sticking yet
    if (sectionViewportTop > stickyTop) {
      sectionNaturalDocY = sectionViewportTop + window.scrollY;
    } else if (!sectionNaturalDocY && window.scrollY > 0) {
      // Page loaded already scrolled past sticky point - use sentinel divider to find natural doc position
      const sentinel = sentinelClass ? document.querySelector(`.${sentinelClass}`) : null;
      if (sentinel) {
        sectionNaturalDocY = sentinel.getBoundingClientRect().bottom + window.scrollY;
      }
    }

    if (!sectionNaturalDocY || window.scrollY === 0) {
      headerBottom.style.height = '';
      headerBottom.style.overflow = '';
      document.body.classList.remove('at-sticky-nav-active');
      return;
    }

    // How much of the header-bottom should remain visible
    const naturalTop = sectionNaturalDocY - window.scrollY;
    const newHeight = Math.max(0, Math.min(headerBottomNaturalHeight, naturalTop - stickyTop));

    headerBottom.style.height = `${newHeight}px`;
    headerBottom.style.overflow = 'hidden';
    document.body.classList.toggle('at-sticky-nav-active', newHeight === 0);
  };

  window.addEventListener('scroll', update, { passive: true });
  update();
}

/**
 * Appends navigation links and initializes observer
 */
function setupNavigation() {
  const navInner = document.querySelector('.at-scroll-to-section__inner');
  const parentSection = navInner?.closest('section');
  if (!parentSection) return;
  const sentinelClass = navInner.dataset.sentinelClass || null;
  setupHeaderBottomHiding(parentSection, sentinelClass);
  const siblingSections = getAllSiblingSections(parentSection);
  const navigationLinks = createNavigationLinks(siblingSections);
  if (navigationLinks.length < 1) {
    parentSection.classList.add('visually-hidden');
    return;
  }
  addScrollBehavior(navigationLinks);
  updateNavigationUI(navigationLinks);
  addIntersectionObserver(siblingSections, navigationLinks);
  addResizeObserver();
  PreventSubnavigationHiding();
}

/**
 * Init
 */
function init() {
  setupNavigation();
}

init();
